import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, User, CreditCard, ClipboardCheck, FileText as FileIcon, DollarSign, Clock, ArrowUpDown, ArrowUp, ArrowDown, Mail, MessageCircle, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Formatea "tiempo transcurrido" desde un ISO en español.
// Retorna { label, color, minutes }
function tiempoTranscurrido(iso) {
  if (!iso) return { label: "—", color: "#94a3b8", minutes: 0 };
  const t = new Date(iso).getTime();
  if (isNaN(t)) return { label: "—", color: "#94a3b8", minutes: 0 };
  const diffMs = Date.now() - t;
  const mins = Math.max(0, Math.floor(diffMs / 60000));
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  let label;
  if (mins < 1) label = "hace instantes";
  else if (mins < 60) label = `hace ${mins} min`;
  else if (hours < 24) {
    const m = mins % 60;
    label = m > 0 ? `hace ${hours}h ${m}min` : `hace ${hours}h`;
  } else if (days < 7) {
    const h = hours % 24;
    label = h > 0 ? `hace ${days}d ${h}h` : `hace ${days}d`;
  } else {
    label = `hace ${days} días`;
  }
  // Color por urgencia: verde <4h, ámbar <24h, rojo >=24h
  const color = hours < 4 ? "#059669" : hours < 24 ? "#d97706" : "#dc2626";
  return { label, color, minutes: mins, hours };
}

// Definición visual de los 5 estatus (orden estricto)
export const PAGO_ESTATUS = [
  { k: "servicio_solicitado", label: "Servicio solicitado", icon: ClipboardCheck, color: "#3b82f6" },
  { k: "cliente_contactado", label: "Cliente contactado", icon: User, color: "#8b5cf6" },
  { k: "info_pago_enviada", label: "Info de pago enviada", icon: FileIcon, color: "#f59e0b" },
  { k: "pago_recibido", label: "Pago recibido", icon: DollarSign, color: "#10b981" },
  { k: "facturado", label: "Facturado", icon: CreditCard, color: "#0f766e" },
];
const ORDEN = PAGO_ESTATUS.map((s) => s.k);
const IX = (k) => ORDEN.indexOf(k);
const ASIGNACION_DESDE = "cliente_contactado";

/** Panel completo (vertical) para la ficha del viajero. */
export function PagoServiciosPanel({ ps, profileId, users, registroAt, onChange }) {
  const { user } = useAuth();
  const canRollback = user?.role === "admin" || user?.role === "supervisor";
  const seg = ps?.seguimiento || { estatus: "servicio_solicitado" };
  const [estatus, setEstatus] = useState(seg.estatus || "servicio_solicitado");
  const [asignado, setAsignado] = useState(seg.asignado_a_email || "");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [nowTick, setNowTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tSolic = useMemo(() => tiempoTranscurrido(registroAt || (seg.historial?.[0]?.at)), [registroAt, seg.historial, nowTick]);

  useEffect(() => {
    setEstatus(seg.estatus || "servicio_solicitado");
    setAsignado(seg.asignado_a_email || "");
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const currentIx = IX(estatus);
  const asignacionMinIx = IX(ASIGNACION_DESDE);
  const requiereAsignado = currentIx >= asignacionMinIx;

  const canSave = dirty && (!requiereAsignado || !!asignado);

  const guardar = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/viajeros-ext/profiles/${profileId}/pago-seguimiento`, {
        estatus,
        asignado_a_email: asignado || "",
        nota,
      });
      toast.success("Seguimiento actualizado");
      setDirty(false);
      setNota("");
      onChange && onChange(res.data.seguimiento);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 space-y-4" data-testid="pago-servicios-panel">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
          <CreditCard className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Pago de Servicios</p>
          {(ps.servicios || []).length > 1 ? (
            <>
              <p className="text-sm font-semibold text-slate-800">{ps.servicios.length} servicios seleccionados</p>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
                {ps.servicios.map((s, i) => (
                  <li key={i}>• {s.nombre} — <b>${Number(s.costo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</b></li>
                ))}
              </ul>
              <p className="text-sm font-bold text-slate-900 mt-1">
                Total: <b>${Number(ps.total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} {ps.moneda || "MXN"}</b>
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-slate-800">{ps.nombre_servicio || (ps.servicios || [])[0]?.nombre}</p>
              <p className="text-xs text-slate-600 mt-0.5">
                <b className="text-slate-800">${Number(ps.total || ps.costo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {ps.moneda || "MXN"}</b>
              </p>
            </>
          )}
          <p className="text-xs text-slate-500 mt-0.5">
            Contactar en máx {ps.tiempo_contacto_valor} {ps.tiempo_contacto_unidad}
            {ps.medios_contacto?.length ? ` · ${ps.medios_contacto.join(", ")}` : ""}
          </p>
          <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ background: `${tSolic.color}18`, color: tSolic.color }} data-testid="pago-panel-tiempo">
            <Clock className="w-3 h-3" /> {tSolic.label} desde solicitud
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="relative">
        <div className="flex items-center justify-between">
          {PAGO_ESTATUS.map((s, i) => {
            const Icon = s.icon;
            const done = i < currentIx;
            const active = i === currentIx;
            return (
              <div key={s.k} className="flex flex-col items-center flex-1 relative">
                {i > 0 && (
                  <div className={`absolute top-3 right-1/2 w-full h-0.5 -z-10 ${i <= currentIx ? "bg-emerald-500" : "bg-slate-200"}`} style={{ marginRight: 10 }} />
                )}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${done ? "bg-emerald-500 border-emerald-500 text-white" : active ? "bg-white border-2 text-white" : "bg-white border-slate-300 text-slate-400"}`}
                     style={active ? { borderColor: s.color, background: s.color, color: "white" } : {}}>
                  {done ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                </div>
                <p className={`text-[9.5px] mt-1 text-center leading-tight ${active ? "font-bold text-slate-800" : done ? "text-emerald-700" : "text-slate-400"}`} style={{ maxWidth: 60 }}>
                  {s.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Controles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">
            {canRollback ? "Cambiar estatus" : "Avanzar a estatus"}
            {canRollback && <span className="ml-1 text-[9px] normal-case font-semibold text-amber-700">(admin: puede retroceder)</span>}
          </label>
          <Select value={estatus} onValueChange={(v) => { setEstatus(v); setDirty(true); }}>
            <SelectTrigger className="h-9 bg-white" data-testid="pago-estatus-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGO_ESTATUS.map((s) => {
                const currentSeg = IX(seg.estatus || "servicio_solicitado");
                const isBack = IX(s.k) < currentSeg;
                const disabled = isBack && !canRollback;
                return (
                  <SelectItem key={s.k} value={s.k} disabled={disabled}>
                    {s.label}{disabled ? " (no se puede retroceder)" : (isBack && canRollback ? " ↩ retroceder" : "")}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">
            Asignado a {requiereAsignado && <span className="text-red-500">*</span>}
          </label>
          <Select value={asignado || undefined} onValueChange={(v) => { setAsignado(v); setDirty(true); }} disabled={!requiereAsignado && currentIx < asignacionMinIx}>
            <SelectTrigger className="h-9 bg-white" data-testid="pago-asignado-select">
              <SelectValue placeholder={requiereAsignado ? "Selecciona un usuario…" : "Se activa desde 'cliente contactado'"} />
            </SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.email} value={u.email}>
                  {u.name} <span className="text-xs text-slate-400 ml-1">({u.email})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">Nota (opcional)</label>
        <Textarea rows={2} value={nota} onChange={(e) => { setNota(e.target.value); setDirty(true); }} placeholder="Ej. Cliente confirmó pago por transferencia SPEI…" className="text-sm bg-white" data-testid="pago-nota" />
      </div>

      {seg.historial?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-600 font-semibold hover:text-slate-900">Historial ({seg.historial.length})</summary>
          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
            {seg.historial.slice().reverse().map((h, i) => (
              <div key={i} className="border-l-2 border-amber-300 pl-2 py-0.5">
                <p className="text-slate-700"><b>{PAGO_ESTATUS.find((s) => s.k === h.estatus)?.label || h.estatus}</b> — {(h.at || "").slice(0, 16).replace("T", " ")}</p>
                <p className="text-slate-500">Por: {h.por_nombre || h.por_email || "sistema"}{h.asignado_a_nombre ? ` · Asignado: ${h.asignado_a_nombre}` : ""}</p>
                {h.nota && <p className="text-slate-500 italic">"{h.nota}"</p>}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex justify-end">
        <Button onClick={guardar} disabled={!canSave || saving} className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid="pago-guardar-seguimiento">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
          Guardar seguimiento
        </Button>
      </div>
    </div>
  );
}

/** Fila compacta (inline) para el tablero de Pagos. */
export function PagoRowInline({ row, users, onRefresh, nowTick, onOpenDetail }) {
  const { user } = useAuth();
  const canRollback = user?.role === "admin" || user?.role === "supervisor";
  const [estatus, setEstatus] = useState(row.seguimiento_estatus);
  const [asignado, setAsignado] = useState(row.asignado_a_email || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setEstatus(row.seguimiento_estatus);
    setAsignado(row.asignado_a_email || "");
    setDirty(false);
  }, [row.profile_id, row.seguimiento_estatus, row.asignado_a_email]);

  const ix = IX(estatus);
  const requiereAsignado = ix >= IX(ASIGNACION_DESDE);
  const canSave = dirty && (!requiereAsignado || !!asignado);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tSolic = useMemo(() => tiempoTranscurrido(row.registro_at), [row.registro_at, nowTick]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tUpd = useMemo(() => tiempoTranscurrido(row.seguimiento_updated_at), [row.seguimiento_updated_at, nowTick]);

  const guardar = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/viajeros-ext/profiles/${row.profile_id}/pago-seguimiento`, {
        estatus,
        asignado_a_email: asignado || "",
      });
      toast.success(`Actualizado: ${row.folio}`);
      setDirty(false);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  const badgeColor = PAGO_ESTATUS.find((s) => s.k === estatus)?.color || "#64748b";

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pago-row-${row.profile_id}`}>
      <td className="px-3 py-2 text-xs">
        <button
          type="button"
          onClick={() => onOpenDetail && onOpenDetail(row)}
          className="text-left block hover:underline"
          data-testid={`pago-row-abrir-${row.profile_id}`}
          title="Ver detalle, historial y notas del seguimiento"
        >
          <div className="font-bold text-slate-800 text-cyan-800">{row.folio}</div>
          {row.subtipo_nombre && (
            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-cyan-100 text-cyan-800 border border-cyan-200" data-testid={`pago-row-subtipo-${row.profile_id}`}>{row.subtipo_nombre}</span>
          )}
          <div className="text-slate-600">{row.nombre}</div>
        </button>
        {/* Contacto según el medio elegido por el viajero */}
        <div className="mt-1 space-y-0.5">
          {(row.medios_contacto || []).includes("correo") && row.email && (
            <a href={`mailto:${row.email}`} className="flex items-center gap-1 text-[11px] text-slate-600 hover:text-[#0037ff] transition-colors" title="Enviar correo">
              <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="truncate max-w-[180px]">{row.email}</span>
            </a>
          )}
          {(row.medios_contacto || []).includes("whatsapp") && (row.whatsapp || row.telefono) && (
            <a
              href={`https://wa.me/${(row.whatsapp || row.telefono).replace(/[^0-9]/g, "")}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 font-medium transition-colors"
              title="Abrir en WhatsApp"
            >
              <MessageCircle className="w-3 h-3 text-emerald-600 flex-shrink-0" />
              <span>{row.whatsapp || row.telefono}</span>
            </a>
          )}
          {!(row.medios_contacto || []).length && (
            <span className="text-[11px] text-slate-400 italic">Sin medio de contacto</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-xs">
        {(row.servicios || []).length > 1 ? (
          <details>
            <summary className="cursor-pointer font-semibold text-slate-800 hover:text-slate-900">
              {(row.servicios || []).length} servicios seleccionados
            </summary>
            <ul className="mt-1 space-y-0.5 text-[11px]">
              {(row.servicios || []).map((s, i) => (
                <li key={i} className="text-slate-600">• {s.nombre} — <b>${Number(s.costo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</b></li>
              ))}
            </ul>
          </details>
        ) : (
          <div className="font-semibold text-slate-800 max-w-xs truncate" title={row.nombre_servicio}>{row.nombre_servicio}</div>
        )}
        <div className="text-slate-800 font-bold mt-0.5">
          Total: ${Number(row.total || row.costo || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} {row.moneda}
        </div>
        <div className="text-slate-400 text-[11px]">
          Contactar en {row.tiempo_contacto_valor}{row.tiempo_contacto_unidad === "horas" ? "h" : "min"}
        </div>
      </td>
      <td className="px-3 py-2 min-w-[120px]" data-testid={`pago-row-tiempo-${row.profile_id}`}>
        <div className="text-xs">
          <div className="flex items-center gap-1 font-semibold" style={{ color: tSolic.color }} title={row.registro_at ? new Date(row.registro_at).toLocaleString("es-MX") : ""}>
            <Clock className="w-3 h-3" /> {tSolic.label}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">desde solicitud</div>
          {row.seguimiento_updated_at && row.seguimiento_updated_at !== row.registro_at && (
            <div className="text-[10px] text-slate-500 mt-1" title={new Date(row.seguimiento_updated_at).toLocaleString("es-MX")}>
              últ. cambio: {tUpd.label}
            </div>
          )}
        </div>
      </td>
      <td className="px-3 py-2 min-w-[180px]">
        <Select value={estatus} onValueChange={(v) => { setEstatus(v); setDirty(true); }}>
          <SelectTrigger className="h-8 text-xs bg-white" data-testid={`pago-row-estatus-${row.profile_id}`}>
            <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: badgeColor }} />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGO_ESTATUS.map((s) => {
              const currentSeg = IX(row.seguimiento_estatus);
              const isBack = IX(s.k) < currentSeg;
              const disabled = isBack && !canRollback;
              return (
                <SelectItem key={s.k} value={s.k} disabled={disabled}>
                  {s.label}{isBack && canRollback ? " ↩" : ""}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 min-w-[200px]">
        <Select value={asignado || undefined} onValueChange={(v) => { setAsignado(v); setDirty(true); }} disabled={ix < IX(ASIGNACION_DESDE) && !asignado}>
          <SelectTrigger className={`h-8 text-xs bg-white ${requiereAsignado && !asignado ? "border-red-300" : ""}`} data-testid={`pago-row-asignado-${row.profile_id}`}>
            <SelectValue placeholder={requiereAsignado ? "⚠️ Requerido" : "—"} />
          </SelectTrigger>
          <SelectContent>
            {users.map((u) => (
              <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" onClick={guardar} disabled={!canSave || saving} className="h-8 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid={`pago-row-guardar-${row.profile_id}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Guardar"}
        </Button>
      </td>
    </tr>
  );
}

/** Panel de tablero completo — lista todos los pagos de todos los proyectos con esa sección activa. */
export function PagosTab({ projects, canEdit, users: usersProp, refreshKey = 0, onExternalRefresh }) {
  const [projectId, setProjectId] = useState("");
  const [data, setData] = useState(null);
  const [users, setUsers] = useState(usersProp || []);
  const [loading, setLoading] = useState(false);
  const [nowTick, setNowTick] = useState(0);
  const [sortBy, setSortBy] = useState("registro_at");
  const [sortDir, setSortDir] = useState("desc"); // asc | desc
  const [filtroEstatus, setFiltroEstatus] = useState("todos");     // todos | <estatus>
  const [filtroAsignado, setFiltroAsignado] = useState("todos");   // todos | sin_asignar | <email>
  const enabledProjects = useMemo(() => (projects || []).filter((p) => p.pago_servicios_config?.enabled), [projects]);

  // Actualiza los timestamps relativos cada 30 s
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (enabledProjects.length && !projectId) setProjectId(enabledProjects[0].id);
  }, [enabledProjects, projectId]);

  useEffect(() => {
    if (usersProp?.length) { setUsers(usersProp); return; }
    axios.get(`${API}/viajeros-ext/system-users`).then((r) => setUsers(r.data.users || [])).catch(() => setUsers([]));
  }, [usersProp]);

  const load = () => {
    if (!projectId) return;
    setLoading(true);
    axios.get(`${API}/viajeros-ext/projects/${projectId}/pagos`)
      .then((r) => setData(r.data))
      .catch(() => setData({ total: 0, rows: [], counts: {} }))
      .finally(() => setLoading(false));
  };
  useEffect(load, [projectId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      // Default: fechas y montos descendentes; texto ascendente
      setSortDir(col === "registro_at" || col === "total" ? "desc" : "asc");
    }
  };

  const sortedRows = useMemo(() => {
    let rows = [...(data?.rows || [])];
    // 1) Filtros
    if (filtroEstatus !== "todos") {
      rows = rows.filter((r) => r.seguimiento_estatus === filtroEstatus);
    }
    if (filtroAsignado !== "todos") {
      if (filtroAsignado === "sin_asignar") {
        rows = rows.filter((r) => !r.asignado_a_email);
      } else {
        rows = rows.filter((r) => r.asignado_a_email === filtroAsignado);
      }
    }
    // 2) Orden
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (r) => {
      if (sortBy === "seguimiento_estatus") return IX(r.seguimiento_estatus);
      if (sortBy === "registro_at") return new Date(r.registro_at || 0).getTime();
      if (sortBy === "total") return Number(r.total || r.costo || 0);
      const v = r[sortBy];
      return typeof v === "string" ? v.toLowerCase() : v ?? "";
    };
    rows.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return rows;
  }, [data, sortBy, sortDir, filtroEstatus, filtroAsignado]);

  // Lista de asignados presentes en los datos (para el select)
  const asignadosPresentes = useMemo(() => {
    const map = new Map();
    (data?.rows || []).forEach((r) => {
      if (r.asignado_a_email) map.set(r.asignado_a_email, r.asignado_a_nombre || r.asignado_a_email);
    });
    return Array.from(map.entries()).map(([email, name]) => ({ email, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // Popup de detalle del pago (con panel completo: notas + historial + estatus)
  const [detailPago, setDetailPago] = useState(null); // { row, profile, loading }
  const openDetail = async (row) => {
    setDetailPago({ row, profile: null, loading: true });
    try {
      const r = await axios.get(`${API}/viajeros-ext/perfiles/${row.profile_id}`);
      setDetailPago({ row, profile: r.data, loading: false });
    } catch (e) {
      setDetailPago({ row, profile: null, loading: false, error: e.response?.data?.detail || "No se pudo cargar el detalle" });
    }
  };
  const closeDetail = () => setDetailPago(null);

  if (!enabledProjects.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-500">
        <CreditCard className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <p className="font-semibold text-slate-700">No hay proyectos con Pago de Servicios activado</p>
        <p className="text-xs mt-1">Actívalo en la configuración de un proyecto (pestaña Proyectos → Editar → General).</p>
      </div>
    );
  }

  const counts = data?.counts || {};
  const hayFiltro = filtroEstatus !== "todos" || filtroAsignado !== "todos";

  const exportarExcel = () => {
    const rows = sortedRows;
    if (!rows.length) return;
    const estatusLbl = (k) => PAGO_ESTATUS.find((s) => s.k === k)?.label || k;
    const proj = enabledProjects.find((p) => p.id === projectId);
    const data = rows.map((r, i) => {
      const servicios = (r.servicios && r.servicios.length ? r.servicios : (r.nombre_servicio ? [{ nombre: r.nombre_servicio, costo: r.costo }] : [])).map((s) => s.nombre).filter(Boolean).join(", ");
      const total = Number(r.total || r.costo || 0);
      return {
        "#": i + 1,
        "Folio": r.folio || "",
        "Subtipo": r.subtipo_nombre || "",
        "Viajero": r.nombre || "",
        "Empresa": r.empresa || "",
        "Email": r.email || "",
        "Teléfono": r.telefono || "",
        "WhatsApp": r.whatsapp || "",
        "Medios de contacto": (r.medios_contacto || []).join(", "),
        "Servicio(s)": servicios,
        "Total": total,
        "Moneda": r.moneda || "MXN",
        "Tiempo de contacto": r.tiempo_contacto_valor ? `${r.tiempo_contacto_valor} ${r.tiempo_contacto_unidad || "horas"}` : "",
        "Estatus de seguimiento": estatusLbl(r.seguimiento_estatus),
        "Asignado a": r.asignado_a_nombre || r.asignado_a_email || "",
        "Registro (fecha)": (r.registro_at || "").slice(0, 19).replace("T", " "),
        "Última actualización": (r.seguimiento_updated_at || "").slice(0, 19).replace("T", " "),
      };
    });
    // Encabezado meta arriba
    const meta = [
      ["Reporte de Pago de Servicios"],
      ["Proyecto:", proj?.nombre || ""],
      ["Cliente:", proj?.cliente_nombre || ""],
      ["Fecha de exportación:", new Date().toISOString().slice(0, 19).replace("T", " ")],
      ["Filtro estatus:", filtroEstatus === "todos" ? "Todos" : estatusLbl(filtroEstatus)],
      ["Filtro asignado:", filtroAsignado === "todos" ? "Todos" : (filtroAsignado === "sin_asignar" ? "Sin asignar" : (asignadosPresentes.find((a) => a.email === filtroAsignado)?.name || filtroAsignado))],
      ["Ordenado por:", `${sortBy} (${sortDir})`],
      ["Total de registros:", rows.length],
      [""],
    ];
    const ws = XLSX.utils.aoa_to_sheet(meta);
    XLSX.utils.sheet_add_json(ws, data, { origin: -1 });
    // Ancho de columnas aprox
    ws["!cols"] = [{ wch: 5 }, { wch: 12 }, { wch: 15 }, { wch: 28 }, { wch: 22 }, { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 40 }, { wch: 12 }, { wch: 8 }, { wch: 20 }, { wch: 22 }, { wch: 22 }, { wch: 20 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pagos");
    const today = new Date().toISOString().slice(0, 10);
    const slug = (proj?.nombre || "proyecto").replace(/[^A-Za-z0-9]+/g, "_").slice(0, 40);
    XLSX.writeFile(wb, `Pagos_${slug}_${today}.xlsx`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px]">
          <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">Proyecto</label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="h-9 bg-white" data-testid="pagos-select-proyecto"><SelectValue /></SelectTrigger>
            <SelectContent>
              {enabledProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">Filtrar por estatus</label>
          <Select value={filtroEstatus} onValueChange={setFiltroEstatus}>
            <SelectTrigger className="h-9 bg-white" data-testid="pagos-filtro-estatus"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los estatus</SelectItem>
              {PAGO_ESTATUS.map((s) => (
                <SelectItem key={s.k} value={s.k}>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                    {s.label} {counts[s.k] > 0 ? <span className="text-slate-400 text-[11px]">({counts[s.k]})</span> : null}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px]">
          <label className="text-[11px] font-bold uppercase text-slate-500 mb-1 block">Filtrar por asignado</label>
          <Select value={filtroAsignado} onValueChange={setFiltroAsignado}>
            <SelectTrigger className="h-9 bg-white" data-testid="pagos-filtro-asignado"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="sin_asignar">
                <span className="text-slate-500 italic">— Sin asignar —</span>
              </SelectItem>
              {asignadosPresentes.map((a) => (
                <SelectItem key={a.email} value={a.email}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hayFiltro && (
          <Button type="button" size="sm" variant="outline" onClick={() => { setFiltroEstatus("todos"); setFiltroAsignado("todos"); }} className="h-9 text-xs" data-testid="pagos-filtros-limpiar">
            ✕ Limpiar filtros
          </Button>
        )}
        <div className="ml-auto flex items-center gap-3 pb-1">
          <div className="text-xs text-slate-500">
            {loading ? "Cargando…" : (
              hayFiltro
                ? `${sortedRows.length} de ${data?.total || 0} solicitud${(data?.total || 0) === 1 ? "" : "es"}`
                : `${data?.total || 0} solicitud${(data?.total || 0) === 1 ? "" : "es"} de pago`
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={exportarExcel}
            disabled={loading || !sortedRows.length}
            className="h-9 text-xs border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 disabled:opacity-50"
            data-testid="pagos-export-excel"
            title="Exportar la vista actual (con los filtros aplicados) a Excel"
          >
            <Download className="w-4 h-4 mr-1.5" /> Exportar Excel
          </Button>
        </div>
      </div>

      {/* KPI cards por estatus (clickables para filtrar) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {PAGO_ESTATUS.map((s) => {
          const c = counts[s.k] || 0;
          const Icon = s.icon;
          const active = filtroEstatus === s.k;
          return (
            <button
              type="button"
              key={s.k}
              onClick={() => setFiltroEstatus(active ? "todos" : s.k)}
              className={`rounded-lg border p-3 text-left transition-all ${active ? "ring-2 ring-offset-1 shadow-md" : "border-slate-200 bg-white hover:border-slate-300"}`}
              style={active ? { borderColor: s.color, ringColor: s.color, background: `${s.color}0d` } : {}}
              data-testid={`pagos-kpi-${s.k}`}
              title={active ? "Clic para quitar filtro" : `Clic para filtrar por ${s.label}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md flex items-center justify-center text-white flex-shrink-0" style={{ background: s.color }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase font-bold text-slate-500 leading-tight">{s.label}</p>
                  <p className="text-lg font-bold text-slate-900 leading-tight">{c}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Tabla */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-600">
            <tr>
              <SortableTh label="Viajero" col="nombre" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortableTh label="Servicio" col="total" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortableTh label="Tiempo" col="registro_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortableTh label="Estatus" col="seguimiento_estatus" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortableTh label="Asignado a" col="asignado_a_nombre" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2 text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400 text-xs">
                {hayFiltro ? "No hay solicitudes que coincidan con los filtros." : "No hay solicitudes de pago aún en este proyecto."}
              </td></tr>
            ) : (
              sortedRows.map((r) => (
                <PagoRowInline
                  key={r.profile_id}
                  row={r}
                  users={users}
                  onRefresh={() => { load(); onExternalRefresh && onExternalRefresh(); }}
                  nowTick={nowTick}
                  onOpenDetail={openDetail}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Popup con detalle completo (panel PagoServiciosPanel: estatus + asignado + nota + historial) */}
      <Dialog open={!!detailPago} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pagos-detalle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <CreditCard className="w-5 h-5" />
              {detailPago?.row?.folio} — {detailPago?.row?.nombre || "—"}
            </DialogTitle>
          </DialogHeader>
          {detailPago?.loading && (
            <div className="py-10 text-center text-slate-500 text-sm">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-amber-600" />
              Cargando detalle…
            </div>
          )}
          {detailPago?.error && (
            <div className="rounded-lg border border-red-200 bg-red-50/60 p-3 text-sm text-red-700">{detailPago.error}</div>
          )}
          {detailPago?.profile && detailPago.profile.pago_servicios?.requiere && (
            <div className="space-y-3">
              {/* Contexto rápido: contacto */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                {detailPago.row?.email && (
                  <a href={`mailto:${detailPago.row.email}`} className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 hover:border-blue-400">
                    <Mail className="w-3.5 h-3.5 text-slate-500" />
                    <span className="truncate">{detailPago.row.email}</span>
                  </a>
                )}
                {(detailPago.row?.whatsapp || detailPago.row?.telefono) && (
                  <a href={`https://wa.me/${(detailPago.row.whatsapp || detailPago.row.telefono).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 hover:border-emerald-500 text-emerald-800">
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>{detailPago.row.whatsapp || detailPago.row.telefono}</span>
                  </a>
                )}
                {detailPago.row?.empresa && (
                  <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5">
                    <User className="w-3.5 h-3.5 text-slate-500" />
                    <span className="truncate">{detailPago.row.empresa}</span>
                  </div>
                )}
              </div>
              <PagoServiciosPanel
                ps={detailPago.profile.pago_servicios}
                profileId={detailPago.profile.id}
                users={users}
                registroAt={detailPago.profile.registro_at}
                onChange={(newSeg) => {
                  // Actualiza el snapshot local y refresca datos externos
                  setDetailPago((cur) => cur ? { ...cur, profile: { ...cur.profile, pago_servicios: { ...cur.profile.pago_servicios, seguimiento: newSeg } } } : cur);
                  load();
                  onExternalRefresh && onExternalRefresh();
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente de cabecera ordenable
function SortableTh({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className="px-3 py-2 text-left cursor-pointer select-none hover:bg-slate-100 transition-colors"
      onClick={() => onSort(col)}
      data-testid={`pagos-sort-${col}`}
      title={active ? `Ordenado ${sortDir === "asc" ? "ascendente" : "descendente"} · clic para invertir` : "Clic para ordenar"}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`w-3 h-3 ${active ? "text-slate-900" : "text-slate-400"}`} />
      </span>
    </th>
  );
}
